import { StatusDropdown } from "components";
import { PreviewPanelDetailRow } from "components/Atoms/details/PreviewPanelDetailRow";
import UserWithAvatar from "components/Atoms/UserWithAvatar";
import { useFreefieldState } from "components/Freefields/hooks";
import { GoogleMapLocation } from "components/GoogleMap/GoogleMapLocation";
import { ViewOnMap } from "components/GoogleMap/ViewOnMap";
import { PreviewPanelInPageEditableField } from "components/MainSectionTemplate/InPageEditableField";
import { PreviewPanelSectionTemplate } from "components/MainSectionTemplate/PreviewPanelSectionTemplate";
import { RenderEditOptions } from "components/MainSectionTemplate/types";
import { ShowDates } from "components/showdatepicker/models";
import { ShowDateAndTimePicker } from "components/showdatepicker/ShowDateAndTimePicker";
import Status from "components/Status/Status";
import TagList from "components/Tag/TagList";
import OptionalTextInput from "components/ValidatedFields/OptionalTextInput";
import { ExclusivityConflictsForShowPreviewPanel } from "exclusivityclause/ExclusivityConflictsForShowPreviewPanel";
import { ExclusivityCheckFields } from "exclusivityclause/utils";
import React, { useEffect, useState } from "react";
import { ShowDateTimeShowInfo } from "show/components/ShowDateTime";
import { mapShowInfoToUpdateShowInternalInstruction, mapShowToShowInfo, ShowInfo } from "show/edit/utils";
import { formatCurrency } from "utilities";
import { BookkeepingPreview } from "../Bookkeeping/BookkeepingPreviewPanel";
import { ArtistPreview, PromoterPreview } from "../Company/CompanyPreviewPanel";
import { PreviewPanelContactList } from "../contact/PreviewPanelContactList";
import { PreviewPanelVerticalDivider } from "../PreviewPanel";
import { PreviewPanelFreeFieldSections } from "../PreviewPanelFreeFieldSection";
import { ProductionPreview } from "../Production/ProductionPreviewPanel";
import { VenuePreview } from "../Venue/VenuePreviewPanel";
import NotValidatedRow from "components/DetailPage/NotValidatedRow";
import moment from "moment";

interface ShowPreviewPanelContentProps {
  show: server.dto.Show
  exclusivityClause: server.dto.ExclusivityClause
  freeFieldCategories: server.dto.FreeFieldCategorySmall[]
  freeFieldPermissions: server.dto.FreeFieldDefinitionPermission[]
  displayFinancials: boolean
  salesExTax: number | null
  costsExTax: number | null
  profitExTax: number | null
  isInternalUser: boolean

  onChange: () => void;
  canEdit: boolean;
}

const getUpdateShowInternalInstruction = (showInfo: ShowInfo, freeFields: server.dto.FreeFieldValue[], show: server.dto.Show): server.dto.UpdateShowInternalEnvironmentInstruction => ({
  showId: show.showId,
  ...mapShowInfoToUpdateShowInternalInstruction(showInfo, freeFields, show.production),
})

export const ShowPreviewPanelContent = (props: ShowPreviewPanelContentProps) => {
  const {
    show,
    exclusivityClause,
    freeFieldCategories,
    freeFieldPermissions,
    displayFinancials,
    salesExTax,
    costsExTax,
    profitExTax,
    isInternalUser,
  } = props;
  const [showInfo, setShowInfo] = useState<ShowInfo>(mapShowToShowInfo(show));
  const freefieldState = useFreefieldState(show.freeFields, freeFieldCategories, freeFieldPermissions);

  const exclusivityCheckFields: ExclusivityCheckFields = {
    artistId: showInfo.artist?.artistId,
    excludeShowId: show.showId,
    exclusivityClause,
    showStartDisplay: showInfo.showDates.startDisplayDate,
    showEndDisplay: showInfo.showDates.endDisplayDate,
    // TODO: These two should come from the show info
    venueLocation: show.venueGeoLocation,
    venueId: show.venue?.companyId,
    appointmentPhase: showInfo.status.appointmentPhase,
  }

  useEffect(() => {
    setShowInfo(mapShowToShowInfo(show));
  }, [props.show]);

  const onValueChanged = (key: string, value: any) => {
    if (key === "eventName") {
      setShowInfo({ ...showInfo, eventName: value });
    } else if (key === "status") {
      setShowInfo({ ...showInfo, status: value });
    } else if (key === "dateAndTime") {
      setShowInfo({ ...showInfo, showDates: value });
    }
  }

  const onSuccessfulUpdate = async () => {
    props.onChange();
  }

  const canInPageEdit = props.canEdit && show.permissions.canModify && isInternalUser;
  const canApprove = show.permissions.canApprove && isInternalUser;

  const approvelCardSubLabel = show.validationStatus === "notValidatedInputByExternal"
    ? `${RESX.Planning.InsertedBy} ${show.createdUser.fullName} ${moment.utc(show.createdUtc).fromNow()}`
    : `${RESX.Planning.InputFromWebform}`;

  return <PreviewPanelSectionTemplate enabled={canInPageEdit} onValueChanged={onValueChanged} onSuccessfulUpdate={onSuccessfulUpdate}>
    <div className="flex-column gap-larger">

      {!show.isValidated && <PreviewPanelDetailRow>
        <NotValidatedRow
          canApprove={canApprove}
          sublabel={approvelCardSubLabel}
          approveEndpoint={() => window.CoreApi.Show.Approve({
            showId: props.show.showId,
          })} />
      </PreviewPanelDetailRow>}

      {show.artist && <PreviewPanelDetailRow labelTop={site.currentEnvironment.functionGroups.artist.singular}>
        <ArtistPreview
          companyId={show.artist.companyId}
          trigger={<a>{show.artist.name}</a>}
          fallbackIfNoPermission={<>{show.artist.name}</>}
        />
      </PreviewPanelDetailRow>}

      {show.attendees.length > 0 && <PreviewPanelDetailRow labelTop={RESX.Planning.Attendees}>
        <PreviewPanelContactList contacts={show.attendees} />
      </PreviewPanelDetailRow>}

      <PreviewPanelDetailRow labelTop={RESX.DateTime.lblDate}>
        <PreviewPanelInPageEditableField
          field={{
            key: "dateAndTime",
            render: () => <ShowDateTimeShowInfo showDates={showInfo.showDates} />,
            inPageEdit: {
              render: ({ value, onChange }: RenderEditOptions<ShowDates>) => <div style={{ paddingLeft: 0 }} className="main-section-template-fields-padding">
                <ShowDateAndTimePicker
                  timePickerPlacehoder={RESX.Show.TimeOfPerformance}
                  showDates={value}
                  onChange={(showDates) => { onChange(showDates); }}
                />
              </div>,
              fullWidth: true,
              type: "confirmOrCancelButton",
              initialValue: showInfo.showDates,
              submitChange: (showDates: ShowDates) => window.CoreApi.Show.UpdateForInternalEnvironment(
                getUpdateShowInternalInstruction({ ...showInfo, showDates }, freefieldState.editableFreefieldValues, show),
              )
            }
          }}
        />
      </PreviewPanelDetailRow>

      <PreviewPanelDetailRow labelTop={RESX.Status.resxStatus}>
        <PreviewPanelInPageEditableField
          field={{
            key: "status",
            render: () => <Status status={showInfo.status} displayPhaseIcon />,
            inPageEdit: {
              render: ({ value, onChange, cancel }: RenderEditOptions<server.dto.StatusSmall>) => <StatusDropdown
                mainArea="show"
                options={undefined}
                value={value}
                onChange={(value) => onChange(value, true)}
                onEsc={() => cancel()}
                config={{
                  focusOnMount: true,
                  openMenuOnFocus: true,
                }}
              />,
              fullWidth: true,
              type: "custom",
              initialValue: showInfo.status,
              submitChange: (status: server.dto.StatusSmall) => window.CoreApi.Show.UpdateForInternalEnvironment(
                getUpdateShowInternalInstruction({ ...showInfo, status }, freefieldState.editableFreefieldValues, show)
              )
            }
          }}
        />
      </PreviewPanelDetailRow>

      {isInternalUser && <ExclusivityConflictsForShowPreviewPanel
        show={show}
        exclusivityCheckFields={exclusivityCheckFields}
      />}

      <PreviewPanelDetailRow labelTop={RESX.Show.NameOfEvent}>
        <PreviewPanelInPageEditableField
          field={{
            key: "eventName",
            isEmpty: !showInfo.eventName,
            render: () => <>{showInfo.eventName}</>,
            inPageEdit: {
              render: ({ value, onChange, submit, cancel }) => <OptionalTextInput
                focusOnMount
                maxLength={serverReference.constants.show.maxEventNameLength}
                value={value}
                onChange={(title) => { onChange(title); }}
                onEnter={() => submit()}
                onEsc={() => cancel()}
              />,
              fullWidth: true,
              type: "confirmOrCancelButton",
              initialValue: showInfo.eventName ?? "",
              submitChange: (eventName: string) => window.CoreApi.Show.UpdateForInternalEnvironment(
                getUpdateShowInternalInstruction({ ...showInfo, eventName }, freefieldState.editableFreefieldValues, show),
              )
            }
          }}
        />
      </PreviewPanelDetailRow>

      {show.venue && <PreviewPanelDetailRow
        labelTop={site.currentEnvironment.functionGroups.venue.singular}
      >
        <VenuePreview
          venueId={show.venue.companyId}
          trigger={<>
            <a>{show.venue.name}</a>
            <div className="text-sublabel">{show.venue.cityCountry}</div>
          </>}
          fallbackIfNoPermission={<>
            <div>{show.venue.name}</div>
            <div className="text-sublabel">{show.venue.cityCountry}</div>
          </>}
        />
      </PreviewPanelDetailRow>}

      {show.promoter && <PreviewPanelDetailRow
        labelTop={site.currentEnvironment.functionGroups.promoter.singular}>
        <PromoterPreview
          companyId={show.promoter.companyId}
          trigger={<a>{show.promoter.name}</a>}
          fallbackIfNoPermission={<>{show.promoter.name}</>}
        />
      </PreviewPanelDetailRow>}

      {show.production && <PreviewPanelDetailRow
        labelTop={site.currentEnvironment.production.singular}>
        <ProductionPreview
          productionId={show.production.productionId}
          trigger={<a>{show.production.name}</a>}
          fallbackIfNoPermission={<span>{show.production.name}</span>}
        />
      </PreviewPanelDetailRow>}

      <PreviewPanelDetailRow labelTop={RESX.GeneralLabels.Owner}>
        <UserWithAvatar user={show.owner} avatarSize="avatar-30" />
      </PreviewPanelDetailRow>

      {(show.bookkeeping && isInternalUser) && <PreviewPanelDetailRow
        labelTop={RESX.Bookkeeping.resxBookkeeping}>
        <BookkeepingPreview
          bookkeepingId={show.bookkeeping.bookkeepingId}
          trigger={<>
            <a>{show.bookkeeping.name}</a>
            <div className="text-sublabel">{show.bookkeeping.code}</div>
          </>}
          fallbackIfNoPermission={<>
            <div>{show.bookkeeping.name}</div>
            <div className="text-sublabel">{show.bookkeeping.code}</div>
          </>}
        />
      </PreviewPanelDetailRow>}

      {(show.costCenter && isInternalUser) && <PreviewPanelDetailRow
        labelTop={RESX.CostCenter.resxCostCenter}
      >
        <div>{show.costCenter.title}</div>
        <div className="text-sublabel">{show.costCenter.internalId}</div>
      </PreviewPanelDetailRow>}

      {displayFinancials && <div className="flex gap-larger">
        {salesExTax != null && <PreviewPanelDetailRow labelTop={RESX.Financial.Sales}>
          <span>{formatCurrency(salesExTax, show.currency, { hideCents: true })}</span>
        </PreviewPanelDetailRow>}
        {profitExTax != null && <PreviewPanelVerticalDivider />}
        {costsExTax != null && <PreviewPanelDetailRow labelTop={RESX.Financial.Costs}>
          <span>{formatCurrency(costsExTax, show.currency, { hideCents: true })}</span>
        </PreviewPanelDetailRow>}
        {profitExTax != null && <>
          <PreviewPanelVerticalDivider />
          <PreviewPanelDetailRow labelTop={RESX.Financial.Profit}>
            <span>{formatCurrency(profitExTax, show.currency, { hideCents: true })}</span>
          </PreviewPanelDetailRow>
        </>}
      </div>}

      {(show.projectCode && isInternalUser) && <PreviewPanelDetailRow
        labelTop={RESX.Show.ProjectCode}>
        <span className="subtitle-text-regular">{show.projectCode}</span>
      </PreviewPanelDetailRow>}

      <PreviewPanelDetailRow
        labelTop={RESX.SelectionManager.Publication}>
        <span className="subtitle-text-regular">{show.publication.text}</span>
      </PreviewPanelDetailRow>

      {show.tags.length > 0 && <PreviewPanelDetailRow labelTop={RESX.Tag.resxTag}>
        <TagList tags={show.tags} length="short" />
      </PreviewPanelDetailRow>}

      <PreviewPanelFreeFieldSections categories={freeFieldCategories} freefieldValues={show.freeFields} />

      {show.venueGeoLocation && <>
        <ViewOnMap height={400}>
          <GoogleMapLocation pin={{
            type: "show",
            phase: "unknown"
          }} location={show.venueGeoLocation} />
        </ViewOnMap>
      </>}
    </div>
  </PreviewPanelSectionTemplate>
}
